import { normalize } from 'normalizr'
import { Schemas } from 'redux/schema'
import { updateEntities, deleteEntity } from 'redux/entities'

import api from 'utils/api'
import apiV2 from 'utils/apiV2'

import { handleError } from '../utils'

const FETCH_REQUEST = 'contenthouse/orderItem/FETCH_REQUEST'
const FETCH_SUCCESS = 'contenthouse/orderItem/FETCH_SUCCESS'
const FETCH_FAILURE = 'contenthouse/orderItem/FETCH_FAILURE'

const CREATE_REQUEST = 'contenthouse/orderItem/CREATE_REQUEST'
const CREATE_SUCCESS = 'contenthouse/orderItem/CREATE_SUCCESS'
const CREATE_FAILURE = 'contenthouse/orderItem/CREATE_FAILURE'

const UPDATE_REQUEST = 'contenthouse/orderItem/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'contenthouse/orderItem/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'contenthouse/orderItem/UPDATE_FAILURE'

const DELETE_REQUEST = 'contenthouse/orderItem/DELETE_REQUEST'
const DELETE_SUCCESS = 'contenthouse/orderItem/DELETE_SUCCESS'
const DELETE_FAILURE = 'contenthouse/orderItem/DELETE_FAILURE'

// Initial State
const initialState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loading: false,
  result: {},
  updating: false,
}

// Actions
export function fetchRequest() {
  return {
    type: FETCH_REQUEST,
  }
}

export function fetchSuccess(payload = {}) {
  return {
    type: FETCH_SUCCESS,
    payload,
  }
}

export function fetchFailure(errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadProjectOrderItems(projectId, options) {
  return (dispatch) => {
    dispatch(fetchRequest())

    const promise = api(`/projects/${projectId}/orders/items`, options)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.ORDER_ITEM_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess(data))

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false, result: errors }
      })

    return promise
  }
}

export function loadOrderItems(options) {
  return (dispatch) => {
    dispatch(fetchRequest())

    const promise = apiV2(`/orders/items`, options)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.ORDER_ITEM_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess(data))

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false, result: errors }
      })

    return promise
  }
}

export function createOrderItem(payload, options = {}) {
  const config = {
    method: 'POST',
    data: payload,
  }

  const { merchandiseId, order, projectId, orderStatus } = payload

  return (dispatch) => {
    dispatch(createRequest())

    const promise = api(`/orders/${order}/items/merchandise/${merchandiseId}`, options, config)
      .then(({ data }) => {
        //These are not included on return at the moment but needed for loading efficiently
        data.project = projectId
        data.order_status = orderStatus

        const normalizedJson = normalize(data, Schemas.ORDER_ITEM)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true, result: data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function updateOrderItem(orderItem, options = {}) {
  const config = {
    method: 'PUT',
    data: orderItem,
  }

  return (dispatch) => {
    dispatch(updateRequest())

    const promise = api(`/orders/items/${orderItem.id}`, options, config)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.ORDER_ITEM)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true, result: data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function deleteOrderItem(orderItem) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch) => {
    dispatch(deleteRequest())

    const promise = apiV2(`/order-items/${orderItem.id}`, { permanent: true }, config)
      .then(({ data }) => {
        const normalizedJson = normalize(orderItem, Schemas.ORDER_ITEM)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function orderFavouriteSet(favourite, options = {}) {
  const config = {
    method: 'POST',
  }

  return (dispatch) => {
    dispatch(createRequest())

    const promise = apiV2(`/orders/${favourite.order}/favourite/${favourite.id}`, options, config)
      .then(({ data }) => {
        const favouriteOrderItems = data.map((orderItem) => ({
          ...orderItem,
          project: favourite.project,
          order_status: favourite.status,
        }))
        const normalizedJson = normalize(favouriteOrderItems, Schemas.ORDER_ITEM_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true, result: data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(state = initialState, action = {}) {
  const { type, payload, errors } = action

  switch (type) {
    case FETCH_REQUEST:
      return {
        ...state,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
        result: payload.continuationToken,
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors,
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors,
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors,
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors,
      }
    default:
      return state
  }
}
